import * as React from 'react';
import { IConfig } from '@cian/config/shared';

import { IContext, context as Context } from '../../utils/context';
import { IJsonQuery } from '../../types/jsonQuery';
import { ISpecialPromo } from '../../types/specialPromo';
import { TCurrentLocation } from '../../types/location';
import { TModifier } from '../../types/modifier';
import { modifyJsonQuery } from '../../utils/modifyJsonQuery';
import { IMakeRequest } from '../../../../../shared/utils/request';

export interface IOnChangeParameters {
  appliedModifiers: [TModifier, ...TModifier[]];
  nextJsonQuery: IJsonQuery;
}

interface IProviderProps {
  jsonQuery: IJsonQuery;
  currentLocation: TCurrentLocation;
  specialPromo?: ISpecialPromo;
  hasUnderground: boolean;
  hasDistricts: boolean;
  hasHighways: boolean;
  appContext: {
    config: IConfig;
    makeRequest: IMakeRequest;
  };
  onChange(parameters: IOnChangeParameters): void;
  onApply(): void;
  onClear?(): void;
  onApplyClick?(): void;
  onAdvancedFiltersOpen?(): void;
  onAdvancedFiltersClose?(): void;
  onGeoPopupOpen?(): void;
  onUndergroundPopupOpen?(): void;
  onDistrictPopupOpen?(): void;
  onHighwaysPopupOpen?(): void;
}

export const Provider: React.FC<IProviderProps> = ({
  children,
  jsonQuery,
  currentLocation,
  specialPromo,
  hasUnderground,
  hasDistricts,
  hasHighways,
  appContext,
  onChange,
  onApply,
  onClear,
  onApplyClick,
  onAdvancedFiltersOpen,
  onAdvancedFiltersClose,
  onDistrictPopupOpen,
  onGeoPopupOpen,
  onUndergroundPopupOpen,
  onHighwaysPopupOpen,
}) => {
  const handleChange = React.useCallback(
    (...modifiers: [TModifier, ...TModifier[]]) => {
      onChange({
        appliedModifiers: modifiers,
        nextJsonQuery: modifyJsonQuery(jsonQuery, modifiers),
      });
    },
    [jsonQuery, onChange],
  );

  const context: IContext = {
    jsonQuery,
    currentLocation,
    specialPromo,
    hasUnderground,
    hasDistricts,
    hasHighways,
    appContext,
    onChange: handleChange,
    onApply,
    onClear,
    onApplyClick,
    onAdvancedFiltersOpen,
    onAdvancedFiltersClose,
    onDistrictPopupOpen,
    onGeoPopupOpen,
    onUndergroundPopupOpen,
    onHighwaysPopupOpen,
  };

  return <Context.Provider value={context}>{children}</Context.Provider>;
};
